// AudioManager.js
// Example usage:
//
// const p = audio.playMusic(MUSIC.LOBBY, { fadeMs: 400 });
// p.handle.stop(200);          // stop immediately (even if still loading)
// await p;                     // resolves after playback ends/stops (or timeout)
//
// const s = audio.playSfx(SFX.CLICK, { rate: 1.2 });
// const v = audio.playVo(VO.INTRO, { timeoutMs: 6000 });
//
// AbortController:
// const ac = new AbortController();
// const m = audio.playMusic(MUSIC.ROUND, { signal: ac.signal });
// ac.abort();                  // cancels just this track
// await m;                     // resolves after stop

import { Howl, Howler } from "howler";

const DEFAULT_AUDIO_FORMATS = ["mp3", "ogg", "wav"];

// Concurrency caps (oldest-drop when exceeded)
const MAX_CONCURRENT_MUSIC = 2;
const MAX_CONCURRENT_SFX = 4;
const MAX_CONCURRENT_VO = 1;

/* ---------------- Utils ---------------- */

function normalizeDefinition(definitionOrUrl, defaults = {}) {
    if (typeof definitionOrUrl === "string") {
        return { src: [definitionOrUrl], ...defaults };
    }
    if (definitionOrUrl && typeof definitionOrUrl === "object") {
        const url = definitionOrUrl.url ?? definitionOrUrl.src;
        if (typeof url === "string" && url) return { ...defaults, ...definitionOrUrl, src: [url] };
        if (Array.isArray(url) && url.length) return { ...defaults, ...definitionOrUrl, src: url.slice() };
    }
    return null;
}

function clamp01(value) { return Math.min(1, Math.max(0, value)); }
function delay(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }
function cacheKey(def) {
    const s = def?.src;
    return !s ? "" : Array.isArray(s) ? s.join("|") : String(s);
}
function waitForHowlLoad(howl, timeoutMs = 8000) {
    return new Promise((resolve, reject) => {
        let done = false;
        const cleanup = () => {
            try { howl.off("load", onLoad); } catch { }
            try { howl.off("loaderror", onErr); } catch { }
        };
        const onLoad = () => { if (!done) { done = true; cleanup(); resolve(howl); } };
        const onErr = (_id, msg) => { if (!done) { done = true; cleanup(); reject(new Error(msg || "loaderror")); } };
        howl.once("load", onLoad);
        howl.once("loaderror", onErr);
        if (timeoutMs > 0) {
            setTimeout(() => {
                if (!done) { done = true; cleanup(); reject(new Error("load timeout")); }
            }, timeoutMs);
        }
    });
}

/**
 * Handle object returned on any playX call (music/sfx/vo).
 * @typedef {Object} PlayHandle
 * @property {number|null} id - Howler id, available after start.
 * @property {(fadeMs?:number)=>void} stop - Stop this audio now (optional fade).
 * @property {(volume:number, fadeMs?:number)=>void} fadeTo - Fade to a volume.
 * @property {()=>boolean} playing - Whether this audio is currently playing.
 * @property {Promise<{howl:Howl, id:number, startedAt:number}|null>} whenStarted - Resolves when playback begins.
 * @property {Promise<void>} whenEnded - Resolves when playback ends/stops.
 */

/**
 * Create a resolved Promise with a no-op handle (for invalid inputs / failures).
 * @returns {Promise<void> & { handle: PlayHandle }}
 */
function createNoopPlayPromise() {
    const p = Promise.resolve();
    Object.defineProperty(p, "handle", {
        value: {
            get id() { return null; },
            stop() { },
            fadeTo() { },
            playing() { return false; },
            whenStarted: Promise.resolve(null),
            whenEnded: Promise.resolve(),
        },
        enumerable: false,
        configurable: false,
        writable: false,
    });
    return /** @type {any} */ (p);
}

/* =======================================================
 *                    AudioManager
 * ======================================================= */
export class AudioManager {
    constructor() {
        this.isReady = false;

        this.musicDefaultVolume = 0.75;
        this.musicMuted = false;

        this.sfxDefaultVolume = 1.0;
        this.sfxMuted = false;

        this.voDefaultVolume = 0.95;
        this.voMuted = false;

        /** @type {Map<string, Howl>} */
        this.cache = new Map();

        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}[]} */
        this.activeMusic = [];
        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}[]} */
        this.activeSfx = [];
        /** @type {{howl: Howl, id: number, startedAt: number, targetVolume: number}[]} */
        this.activeVo = [];

        this.hasUnlocked = false;
        this.onVisibilityChangeBound = this.onVisibilityChange.bind(this);
    }

    /** Initialize Howler and unlock listeners (idempotent). */
    init() {
        if (this.isReady) return;
        Howler.autoUnlock = true;
        document.addEventListener("visibilitychange", this.onVisibilityChangeBound, false);
        this.wireUnlockListeners();
        this.isReady = true;
    }

    /** Cleanup and unload all audio. */
    destroy() {
        document.removeEventListener("visibilitychange", this.onVisibilityChangeBound);
        this.stopAllMusic();
        this.stopAllSfx();
        this.stopAllVo();
        this.cache.forEach(h => { try { h.unload(); } catch { } });
        this.cache.clear();
        this.activeMusic.length = 0;
        this.activeSfx.length = 0;
        this.activeVo.length = 0;
    }

    wireUnlockListeners() {
        const tryUnlock = () => {
            if (!this.hasUnlocked) {
                try { Howler.mute(false); } catch { }
                this.hasUnlocked = true;
            }
            ["pointerdown", "keydown", "click", "touchstart"].forEach(n => window.removeEventListener(n, tryUnlock));
        };
        ["pointerdown", "keydown", "click", "touchstart"].forEach(n => window.addEventListener(n, tryUnlock, { passive: true }));
    }

    onVisibilityChange() {
        if (document.hidden) {
            this.setMasterMute(true);
        } else {
            this.setMasterMute(false);
        }
    }

    /* ---------------- internals ---------------- */

    _stopEntry(entry, fadeMs = 80) {
        if (!entry?.howl || entry.id == null) return;
        try {
            const v = entry.howl.volume(entry.id);
            try { entry.howl.fade(v, 0, fadeMs, entry.id); } catch { }
            setTimeout(() => { try { entry.howl.stop(entry.id); } catch { } }, fadeMs + 10);
        } catch { }
    }

    _removeById(list, id) {
        const i = list.findIndex(e => e.id === id);
        if (i >= 0) list.splice(i, 1);
    }

    _enforceLimit(list, max, fadeMs = 80) {
        while (list.length >= max) {
            const oldest = list.shift();
            this._stopEntry(oldest, fadeMs);
        }
    }

    _getOrCreateHowl(def, cfg = {}) {
        const key = cacheKey(def);
        if (!key) return null;
        let howl = this.cache.get(key);
        if (!howl) {
            howl = new Howl({
                src: def.src,
                preload: true,
                volume: clamp01(cfg.volume ?? 0), // start silent; we'll fade/set volume after play
                format: DEFAULT_AUDIO_FORMATS,
            });
            this.cache.set(key, howl);
        }
        return howl;
    }

    /* ---------------- Prime / Preload ---------------- */

    /**
     * Preload a single clip. Resolves when loaded (or rejects on error/timeout).
     * @param {string|{src:string[]}|{src:string}|{url:string}} clip
     * @param {{timeoutMs?: number, volume?: number}} [opts]
     * @returns {Promise<Howl>}
     */
    async primeClip(clip, opts = {}) {
        this.init();
        const def = normalizeDefinition(clip);
        if (!def) throw new Error("[AudioManager] primeClip: invalid clip");

        const timeoutMs = opts.timeoutMs ?? 8000;

        const howl = this._getOrCreateHowl(def, { volume: opts.volume ?? 0 });
        const st = howl.state();
        if (st === "loaded") return howl;
        if (st === "unloaded") { try { howl.load(); } catch { } }

        return await waitForHowlLoad(howl, timeoutMs);
    }

    /**
     * Preload many clips. Accepts an array or object map.
     * @param {Array<string|Object>|Record<string,string|Object>} clips
     * @param {{timeoutMs?: number, volume?: number}} [opts]
     * @returns {Promise<void>}
     */
    async prime(clips, opts = {}) {
        if (!clips) return;
        const arr = Array.isArray(clips) ? clips : Object.values(clips);
        await Promise.all(arr.map(c => this.primeClip(c, opts)));
    }

    /* =======================================================
     *                 Generic playback helper
     * ======================================================= */

    /**
     * Internal unified play helper.
     * @param {"music"|"sfx"|"vo"} kind
     * @param {object|string} definitionOrUrl
     * @param {object} options
     * @param {object} behavior - per-kind behavior
     *   {
     *     list: Array, cap: number, fadeCapMs: number,
     *     computeVolume: (def, opts) => number,
     *     isMuted: () => boolean,
     *     onBeforePlay?: (howl, id, def, opts) => void,   // e.g., set rate
     *     loop: boolean,               // default loop behavior for this kind
     *     fadeInMs: number,            // default fade-in
     *   }
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    _play(kind, definitionOrUrl, options, behavior) {
        this.init();

        const def = normalizeDefinition(definitionOrUrl, { loop: behavior.loop });
        if (!def) {
            console.error(`[AudioManager] Invalid ${kind} play call:`, { def, input: definitionOrUrl });
            return createNoopPlayPromise();
        }

        const howl = this._getOrCreateHowl(def, { volume: 0 });
        if (!howl) {
            console.error(`[AudioManager] Failed to create Howl for ${kind}:`, def);
            return createNoopPlayPromise();
        }

        // ---- Build handle (usable before real start) ----
        let entry = null;
        let pendingStopFade = null;
        let startedResolve; let endedResolve;
        const whenStarted = new Promise(res => { startedResolve = res; });
        const whenEnded = new Promise(res => { endedResolve = res; });

        /** @type {PlayHandle} */
        const handle = {
            get id() { return entry?.id ?? null; },
            stop: (fade = 200) => {
                // Notify fade-out start for this particular play call
                try { options?.onFadeOutStart?.(); } catch { }
                if (entry) {
                    this._stopEntry(entry, fade);
                    this._removeById(behavior.list, entry.id);
                    endedResolve?.();
                } else {
                    // If we haven't started yet, mark to stop on (would-be) start
                    pendingStopFade = fade;
                }
            },
            fadeTo: (vol, ms = 300) => {
                if (entry) {
                    const newTarget = clamp01(vol);
                    entry.targetVolume = newTarget;
                    const actualVol = behavior.isMuted() ? 0 : newTarget;
                    try { entry.howl.fade(entry.howl.volume(entry.id), actualVol, ms, entry.id); } catch { }
                }
            },
            playing: () => !!(entry && entry.howl.playing(entry.id)),
            whenStarted,
            whenEnded,
        };

        const signal = options?.signal;
        const loop = options?.loop ?? def.loop ?? behavior.loop;
        const fadeInMs = options?.fadeMs ?? behavior.fadeInMs;
        const targetVol = clamp01(options?.volume ?? def.volume ?? behavior.computeVolume(def, options));
        const timeoutMs = options?.timeoutMs;
        const onFadeInEnd = options?.onFadeInEnd;

        // If already aborted at call time, don't even register load listeners.
        if (signal?.aborted) {
            try { options?.onFadeOutStart?.(); } catch { }
            startedResolve?.(null);
            endedResolve?.();
            const p = Promise.resolve();
            /** @type {any} */ (p).handle = handle;  // <- keep on its own line
            return p;
        }

        const playPromise = new Promise(resolve => {
            let settled = false;
            let timeoutId = null;
            let loadListenerAttached = false;

            const finalize = () => {
                if (settled) return;
                settled = true;
                if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
                resolve();
            };

            const finish = (id) => {
                this._removeById(behavior.list, id);
                try { options?.onEnd?.(); } catch { }
                endedResolve?.();
                finalize();
            };

            const start = () => {
                // Guard: if aborted before we got to start, do not play.
                if (signal?.aborted || pendingStopFade === -1) {
                    try { options?.onFadeOutStart?.(); } catch { }
                    // indicate "cancel before start"
                    startedResolve?.(null);
                    endedResolve?.();
                    finalize();
                    return;
                }

                const id = howl.play();
                howl.once("playerror", (_id, err) => console.error(`[AudioManager] ${kind} playerror:`, err, def.src));
                try { howl.loop(loop, id); } catch { }
                try { howl.volume(0, id); } catch { }

                try { behavior.onBeforePlay?.(howl, id, def, options); } catch { }

                // Determine actual playback volume based on mute state
                const volTo = behavior.isMuted() ? 0 : targetVol;

                // Only fade if not muted, otherwise just set volume to 0
                if (behavior.isMuted()) {
                    try { howl.volume(0, id); } catch { }
                } else {
                    try { howl.fade(0, volTo, fadeInMs, id); }
                    catch { try { howl.volume(volTo, id); } catch { } }
                }

                if (typeof onFadeInEnd === "function") {
                    setTimeout(() => { try { onFadeInEnd(); } catch { } }, fadeInMs + 10);
                }

                entry = { howl, id, startedAt: performance.now?.() ?? Date.now(), targetVolume: targetVol };

                // Concurrency (drop oldest)
                this._enforceLimit(behavior.list, behavior.cap, behavior.fadeCapMs);

                behavior.list.push(entry);
                startedResolve?.(entry);

                // If stop was requested before start, stop immediately without letting it "play"
                if (pendingStopFade != null) {
                    try { options?.onFadeOutStart?.(); } catch { }
                    this._stopEntry(entry, pendingStopFade);
                    this._removeById(behavior.list, id);
                    endedResolve?.();
                    finalize();
                    return;
                }

                // Timeout logic (optionally stop on timeout)
                if (timeoutMs && timeoutMs > 0) {
                    timeoutId = setTimeout(() => {
                        if (options?.stopOnTimeout) {
                            try { options?.onFadeOutStart?.(); } catch { }
                            handle.stop(options?.timeoutFadeMs ?? 120);
                            // stop listener below will finalize
                        } else {
                            finalize(); // just resolve; playback continues
                        }
                    }, timeoutMs);
                }

                const onFinish = () => finish(id);
                // Only listen to 'end' for non-looped audio
                if (!loop) {
                    howl.once("end", onFinish, id);
                }
                howl.once("stop", onFinish, id);
            };

            const onLoad = start;

            // If not loaded yet, start on load; otherwise start now.
            const st = howl.state();
            if (st === "unloaded") { try { howl.load(); } catch { } }
            if (st === "loaded") {
                start();
            } else {
                loadListenerAttached = true;
                howl.once("load", onLoad);
            }

            // Abort handling: before-start cancellation should *not* play anything.
            if (signal) {
                signal.addEventListener("abort", () => {
                    // If we haven't started yet, prevent start and resolve immediately.
                    if (!entry) {
                        try { options?.onFadeOutStart?.(); } catch { }
                        // mark: cancel-before-start (used by start guard above)
                        pendingStopFade = -1;
                        if (loadListenerAttached) {
                            try { howl.off("load", onLoad); } catch { }
                            loadListenerAttached = false;
                        }
                        startedResolve?.(null);
                        endedResolve?.();
                        finalize();
                    } else {
                        // If already started, stop normally.
                        handle.stop(120);
                    }
                }, { once: true });
            }
        });

        /** @type {any} */ (playPromise).handle = handle;
        return playPromise; // timeout handled inside; do NOT wrap with withTimeout
    }


    /* ---------------- MUSIC ---------------- */

    /**
     * Play background music (looped by default).
     * Returns a Promise that resolves when playback ends/stops (or timeout). Use the returned `.handle`
     * for mid-play control, and optional `onFadeInEnd` / `onFadeOutStart` callbacks for UI timing.
     * @param {string|Object} definitionOrUrl
     * @param {{
     *   loop?:boolean,
     *   fadeMs?:number,
     *   volume?:number,
     *   timeoutMs?:number,
     *   stopOnTimeout?:boolean,
     *   timeoutFadeMs?:number,
     *   signal?:AbortSignal,
     *   onEnd?:Function,
     *   onFadeInEnd?:Function,
     *   onFadeOutStart?:Function
     * }} [options]
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    playMusic(definitionOrUrl, options = {}) {
        return this._play("music", definitionOrUrl, options, {
            list: this.activeMusic,
            cap: MAX_CONCURRENT_MUSIC,
            fadeCapMs: options.fadeMs ?? 500,
            computeVolume: (_def, _opts) => this.musicDefaultVolume,
            isMuted: () => this.musicMuted,
            onBeforePlay: (_howl, _id) => { /* no-op for music */ },
            loop: true,
            fadeInMs: options.fadeMs ?? 500,
        });
    }

    /**
     * Stop the most recent music entry.
     * @param {{fadeMs?: number}} [options]
     */
    stopMusic(options = {}) {
        const fadeMs = options.fadeMs ?? 300;
        const last = this.activeMusic.pop();
        if (!last) return;
        this._stopEntry(last, fadeMs);
    }

    /**
     * Stop a specific music entry by id.
     * @param {number} id
     * @param {number} [fadeMs=300]
     */
    stopMusicById(id, fadeMs = 300) {
        const idx = this.activeMusic.findIndex(e => e.id === id);
        if (idx === -1) return;
        const [entry] = this.activeMusic.splice(idx, 1);
        this._stopEntry(entry, fadeMs);
    }

    /** Stop all music immediately. */
    stopAllMusic() {
        this.activeMusic.forEach(e => { try { e.howl.stop(e.id); } catch { } });
        this.activeMusic.length = 0;
    }

    /** Set default music volume (0..1). Applies to current if not muted. */
    setMusicVolume(value) {
        this.musicDefaultVolume = clamp01(value);
        const last = this.activeMusic[this.activeMusic.length - 1];
        if (last?.howl && !this.musicMuted) {
            last.targetVolume = this.musicDefaultVolume;
            last.howl.volume(this.musicDefaultVolume, last.id);
        }
    }

    /** Mute/unmute music (sets volume to 0 but keeps playing). */
    muteMusic(mute = true) {
        this.musicMuted = !!mute;
        this.activeMusic.forEach(e => {
            try {
                const newVol = this.musicMuted ? 0 : e.targetVolume;
                e.howl.volume(newVol, e.id);
            } catch { }
        });
    }

    /* ---------------- SFX ---------------- */

    /**
     * Play a one-shot sound effect.
     * Returns a Promise that resolves when playback ends/stops (or timeout). Optional `onFadeInEnd` /
     * `onFadeOutStart` can be used (though SFX typically use tiny fades).
     * @param {string|Object} definitionOrUrl
     * @param {{
     *   volume?:number,
     *   rate?:number,
     *   timeoutMs?:number,
     *   stopOnTimeout?:boolean,
     *   timeoutFadeMs?:number,
     *   signal?:AbortSignal,
     *   onEnd?:Function,
     *   onFadeInEnd?:Function,
     *   onFadeOutStart?:Function
     * }} [options]
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    playSfx(definitionOrUrl, options = {}) {
        return this._play("sfx", definitionOrUrl, options, {
            list: this.activeSfx,
            cap: MAX_CONCURRENT_SFX,
            fadeCapMs: 60,
            computeVolume: (def, opts) => {
                const base = def.volume != null ? clamp01(def.volume) : this.sfxDefaultVolume;
                const mult = opts?.volume != null ? clamp01(opts.volume) : 1;
                return base * mult;
            },
            isMuted: () => this.sfxMuted,
            onBeforePlay: (howl, id, _def, opts) => {
                const rate = opts?.rate ?? 1.0;
                try { howl.rate(rate, id); } catch { }
            },
            loop: false,
            fadeInMs: 20,
        });
    }

    /** Stop all SFX immediately. */
    stopAllSfx() {
        this.activeSfx.forEach(e => { try { e.howl.stop(e.id); } catch { } });
        this.activeSfx.length = 0;
    }

    /** Set default SFX volume (0..1). */
    setSfxVolume(value) {
        this.sfxDefaultVolume = clamp01(value);
        this.activeSfx.forEach(e => {
            e.targetVolume = this.sfxDefaultVolume;
            if (!this.sfxMuted) {
                try { e.howl.volume(this.sfxDefaultVolume, e.id); } catch { }
            }
        });
    }

    /** Mute/unmute all SFX (sets volume to 0 but keeps playing). */
    muteSfx(mute = true) {
        this.sfxMuted = !!mute;
        this.activeSfx.forEach(e => {
            try {
                const newVol = this.sfxMuted ? 0 : e.targetVolume;
                e.howl.volume(newVol, e.id);
            } catch { }
        });
    }

    /* ---------------- VO ---------------- */

    /**
     * Play a voice-over line.
     * Returns a Promise that resolves when playback ends/stops (or timeout). Use `onFadeInEnd` /
     * `onFadeOutStart` to hook UI transitions around VO.
     * @param {string|Object} definitionOrUrl
     * @param {{
     *   volume?:number,
     *   timeoutMs?:number,
     *   stopOnTimeout?:boolean,
     *   timeoutFadeMs?:number,
     *   signal?:AbortSignal,
     *   onEnd?:Function,
     *   onFadeInEnd?:Function,
     *   onFadeOutStart?:Function
     * }} [options]
     * @returns {Promise<void> & { handle: PlayHandle }}
     */
    playVo(definitionOrUrl, options = {}) {
        return this._play("vo", definitionOrUrl, options, {
            list: this.activeVo,
            cap: MAX_CONCURRENT_VO,
            fadeCapMs: 120,
            computeVolume: (def, opts) => {
                const base = def.volume != null ? clamp01(def.volume) : this.voDefaultVolume;
                const mult = opts?.volume != null ? clamp01(opts.volume) : 1;
                return base * mult;
            },
            isMuted: () => this.voMuted,
            onBeforePlay: (_howl, _id) => { /* no-op for VO */ },
            loop: false,
            fadeInMs: 60,
        });
    }

    /** Stop all currently playing VO immediately. */
    stopAllVo() {
        this.activeVo.forEach(e => { try { e.howl.stop(e.id); } catch { } });
        this.activeVo.length = 0;
    }

    /**
     * Stop the most recent VO (useful on state changes).
     * @param {number} [fadeMs=120]
     */
    stopVo(fadeMs = 120) {
        const last = this.activeVo.pop();
        if (!last) return;
        this._stopEntry(last, fadeMs);
    }

    /** Set default VO volume (0..1). */
    setVoVolume(value) {
        this.voDefaultVolume = clamp01(value);
        this.activeVo.forEach(e => {
            e.targetVolume = this.voDefaultVolume;
            if (!this.voMuted) {
                try { e.howl.volume(this.voDefaultVolume, e.id); } catch { }
            }
        });
    }

    /** Mute/unmute all VO (sets volume to 0 but keeps playing). */
    muteVo(mute = true) {
        this.voMuted = !!mute;
        this.activeVo.forEach(e => {
            try {
                const newVol = this.voMuted ? 0 : e.targetVolume;
                e.howl.volume(newVol, e.id);
            } catch { }
        });
    }

    /* ---------------- Master + Sequence ---------------- */

    /** Stop all audio (music, SFX, and VO) immediately. */
    stopAllAudio() {
        this.stopAllMusic();
        this.stopAllSfx();
        this.stopAllVo();
    }

    /** Mute/unmute all audio globally. */
    setMasterMute(mute = true) {
        Howler.mute(!!mute);
    }

    /**
     * Run a sequence of audio actions in order.
     * Accepts music(), sfx(), vo(), wait(ms), or numbers as delays.
     * @param {Array} steps
     * @returns {Promise<void>}
     */
    async sequence(steps = []) {
        for (const step of steps) {
            if (step == null) continue;
            if (typeof step === "number") { await delay(step); continue; }
            if (step.type === "wait") { await delay(step.ms || 0); continue; }
            if (step.type === "music") { await this.playMusic(step.ref, step.opts || {}); continue; }
            if (step.type === "sfx") { await this.playSfx(step.ref, step.opts || {}); continue; }
            if (step.type === "vo") { await this.playVo(step.ref, step.opts || {}); continue; }
        }
    }
}

/* ---------- Convenience builders for sequence() ---------- */

/**
 * Create a music step for use in an audio sequence.
 * @param {*} ref
 * @param {*} opts
 * @returns {{type:"music", ref:any, opts?:any}}
 */
export function music(ref, opts) { return { type: "music", ref, opts }; }

/**
 * Create a sound effect step for use in an audio sequence.
 * @param {*} ref
 * @param {*} opts
 * @returns {{type:"sfx", ref:any, opts?:any}}
 */
export function sfx(ref, opts) { return { type: "sfx", ref, opts }; }

/**
 * Create a voice-over step for use in an audio sequence.
 * @param {*} ref
 * @param {*} opts
 * @returns {{type:"vo", ref:any, opts?:any}}
 */
export function vo(ref, opts) { return { type: "vo", ref, opts }; }

/**
 * Create a wait/delay step for use in an audio sequence.
 * @param {number} ms
 * @returns {{type:"wait", ms:number}}
 */
export function wait(ms) { return { type: "wait", ms }; }

/** Singleton AudioManager instance. */
export const audio = new AudioManager();